import { motion } from "framer-motion";
import {
  MonitorPlay,
  Volume2,
  Lightbulb,
  Boxes,
  Video,
  ArrowUpRight,
} from "lucide-react";
import { IMAGES } from "@/lib/api";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export function Marquee() {
  const items = [
    "School Annual Days",
    "College Festivals",
    "Concerts",
    "Political Rallies",
    "Convocations",
    "Temple Events",
    "Award Functions",
    "Product Launches",
  ];
  const row = [...items, ...items];
  return (
    <section className="py-8 border-y border-white/10 bg-[#0a0a0a] overflow-hidden" data-testid="marquee">
      <div className="flex whitespace-nowrap animate-marquee">
        {row.map((t, i) => (
          <div key={i} className="flex items-center gap-16 pr-16">
            <span className="font-display text-2xl md:text-3xl text-white/80 tracking-tight">{t}</span>
            <span className="text-[#F5B841] text-xl">✦</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Stats() {
  const stats = [
    { n: "15+", l: "Years of Excellence" },
    { n: "3200+", l: "Sq.Ft. LED Inventory" },
    { n: "500+", l: "Events Produced" },
    { n: "100%", l: "On-Time Setup" },
  ];
  return (
    <section id="about" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="stats-section">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <motion.div {...reveal} className="md:col-span-5">
          <span className="text-xs tracking-[0.35em] text-[#F5B841]">WHO WE ARE</span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight mt-5 leading-tight">
            Your trusted partner for spectacular, flawless events.
          </h2>
        </motion.div>
        <motion.p {...reveal} className="md:col-span-7 md:pl-12 self-end text-white/60 text-lg font-light max-w-xl">
          From vision to standing ovation, Bhaaskar Productions delivers complete event production — engineering light,
          sound and vision into experiences that inspire generations.
        </motion.p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mt-16 border border-white/10">
        {stats.map((s, i) => (
          <motion.div
            key={s.l}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08, duration: 0.6 }}
            className="bg-[#050505] p-8 md:p-10"
          >
            <div className="font-display text-4xl md:text-6xl text-[#F5B841] tracking-tighter">{s.n}</div>
            <div className="text-white/50 text-sm mt-3">{s.l}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const services = [
  { icon: MonitorPlay, title: "LED Video Walls", points: ["Indoor & Outdoor LED", "High-Resolution Panels", "Live Camera Feed (IMAG)", "Large-Scale Event Walls"] },
  { icon: Volume2, title: "Professional Audio", points: ["Line Array Systems", "Digital Audio Mixers", "Wireless Microphones", "DJ & Concert Sound"] },
  { icon: Lightbulb, title: "Stage Lighting", points: ["Moving Head & Beam", "Wash & PAR Lights", "Follow Spots", "Special Effects"] },
  { icon: Boxes, title: "Stage & Truss", points: ["Aluminium Trussing", "Custom Stage Design", "Roof Structures", "Backdrop & Branding"] },
  { icon: Video, title: "Live Event Production", points: ["Live Camera Coverage", "Live Streaming", "Technical Direction", "End-to-End Execution"] },
];

export function Services() {
  return (
    <section id="services" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="services-section">
      <motion.div {...reveal} className="flex items-end justify-between mb-14">
        <div>
          <span className="text-xs tracking-[0.35em] text-[#F5B841]">WHAT WE OFFER</span>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-4">Our Services</h2>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
        {services.map((s, i) => {
          const Icon = s.icon;
          const big = i === 0 || i === 4;
          return (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.6 }}
              data-testid={`service-card-${i}`}
              className={`group relative bg-[#0a0a0a] border border-white/10 p-8 hover:border-[#F5B841]/50 transition-colors ${
                big ? "md:col-span-3" : "md:col-span-2"
              }`}
            >
              <div className="flex items-center justify-between">
                <Icon className="w-8 h-8 text-[#F5B841]" />
                <ArrowUpRight className="w-5 h-5 text-white/20 group-hover:text-[#F5B841] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="font-display text-2xl mt-8 tracking-tight">{s.title}</h3>
              <ul className="mt-5 space-y-2">
                {s.points.map((p) => (
                  <li key={p} className="text-white/50 text-sm flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-[#F5B841]" /> {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

const chapters = [
  { n: "01", t: "Consult & Design", d: "We understand your vision, venue and audience, then design a stage, LED and lighting plan built for impact." },
  { n: "02", t: "Engineer & Build", d: "Truss, screens, line-array sound and rigging installed by a skilled technical crew — safe, secure, on time." },
  { n: "03", t: "Execute & Deliver", d: "Live direction, camera feeds and flawless show control from the first cue to the final standing ovation." },
];

export function Process() {
  return (
    <section id="process" className="bg-[#0a0a0a] border-y border-white/10 py-24 md:py-32" data-testid="process-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div {...reveal}>
          <span className="text-xs tracking-[0.35em] text-[#F5B841]">HOW WE WORK</span>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-4 max-w-2xl">
            From vision to reality, in three deliberate chapters.
          </h2>
        </motion.div>

        <div className="mt-16 divide-y divide-white/10 border-t border-white/10">
          {chapters.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 group"
            >
              <div className="md:col-span-3 font-display text-6xl md:text-8xl text-white/10 group-hover:text-[#F5B841]/80 transition-colors tracking-tighter">
                {c.n}
              </div>
              <h3 className="md:col-span-4 font-display text-2xl md:text-3xl tracking-tight self-center">{c.t}</h3>
              <p className="md:col-span-5 text-white/55 text-lg font-light self-center">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const LED_WALL = "/events/led-wall.jpg";

const shots = [
  { src: LED_WALL, span: "md:col-span-8 md:row-span-2", label: "High-resolution LED video wall setup" },
  { src: IMAGES.hero, span: "md:col-span-4", label: "Concert-grade stage lighting" },
  { src: IMAGES.g1, span: "md:col-span-4", label: "Outdoor annual day setup" },
  { src: IMAGES.g2, span: "md:col-span-6", label: "Branded LED entrance arch" },
  { src: IMAGES.g3, span: "md:col-span-6", label: "Large-scale rally production" },
];

export function Gallery() {
  return (
    <section id="work" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="gallery-section">
      <motion.div {...reveal} className="mb-14">
        <span className="text-xs tracking-[0.35em] text-[#F5B841]">SELECTED WORK</span>
        <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-4">Glimpses of our events</h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[220px]">
        {shots.map((s, i) => (
          <motion.figure
            key={i}
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: i * 0.08, duration: 0.7 }}
            data-testid={`gallery-item-${i}`}
            className={`relative overflow-hidden group border border-white/10 ${s.span}`}
          >
            <img
              src={s.src}
              alt={s.label}
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/90 via-transparent to-transparent" />
            <figcaption className="absolute bottom-5 left-5 text-sm text-white/80 tracking-wide">{s.label}</figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}
